/**
 * Umbrales de cobertura poblacional — modo "Red nacional CALE".
 *
 * Cobertura a 60 / 90 / 120 minutos [DISEÑO]: porcentaje de municipios (y
 * de población de la muestra) cuyo tiempoViajeMin al nodo/sede más cercano
 * es ≤ umbral.
 *
 * Regla de honestidad de datos: los tiempos de viaje de la muestra son
 * null ([POR VERIFICAR] — no existen matrices de tiempo calculadas).
 * Cuando ningún municipio tiene dato, las funciones devuelven NaN (nunca
 * un porcentaje inventado) y el mensaje declara el estado [POR VERIFICAR].
 */

export const UMBRALES_MINUTOS = [60, 90, 120] as const;

export interface MunicipioConTiempo {
  codigoDivipola: string;
  poblacion: number;
  /** Minutos al nodo/sede más cercano; null = [POR VERIFICAR]. */
  tiempoViajeMin: number | null;
}

export interface ResultadoCobertura {
  umbralMin: number;
  /** % de municipios cubiertos. NaN si no hay datos de tiempo. */
  coberturaMunicipiosPct: number;
  /** % de población cubierta. NaN si no hay datos de tiempo. */
  coberturaPoblacionPct: number;
  /** Municipios con dato de tiempo (base del cálculo). */
  conDato: number;
  total: number;
  mensaje: string;
}

export const MENSAJE_SIN_DATOS =
  "[POR VERIFICAR] Sin tiempos de viaje calculados: la cobertura no puede " +
  "computarse y se reporta NaN en lugar de un porcentaje inventado.";

/**
 * Cobertura poblacional para un umbral dado.
 * Con todos los tiempoViajeMin en null devuelve NaN manejado + mensaje.
 * Municipios sin dato se excluyen del denominador y se reportan aparte.
 */
export function coberturaPoblacional(
  municipios: readonly MunicipioConTiempo[],
  umbralMin: number,
): ResultadoCobertura {
  const total = municipios.length;
  const conTiempo = municipios.filter((m) => m.tiempoViajeMin != null);

  if (total === 0 || conTiempo.length === 0) {
    return {
      umbralMin,
      coberturaMunicipiosPct: Number.NaN,
      coberturaPoblacionPct: Number.NaN,
      conDato: 0,
      total,
      mensaje: MENSAJE_SIN_DATOS,
    };
  }

  const cubiertos = conTiempo.filter((m) => (m.tiempoViajeMin as number) <= umbralMin);
  const pobTotal = conTiempo.reduce((acc, m) => acc + m.poblacion, 0);
  const pobCubierta = cubiertos.reduce((acc, m) => acc + m.poblacion, 0);

  return {
    umbralMin,
    coberturaMunicipiosPct: (cubiertos.length / conTiempo.length) * 100,
    coberturaPoblacionPct: pobTotal === 0 ? Number.NaN : (pobCubierta / pobTotal) * 100,
    conDato: conTiempo.length,
    total,
    mensaje:
      conTiempo.length < total
        ? `[POR VERIFICAR] Cobertura calculada solo sobre ${conTiempo.length}/${total} municipios con dato de tiempo.`
        : "Cobertura calculada sobre la totalidad de municipios con dato.",
  };
}

/** Cobertura para los tres umbrales estándar (60/90/120 min). */
export function coberturaUmbralesEstandar(
  municipios: readonly MunicipioConTiempo[],
): ResultadoCobertura[] {
  return UMBRALES_MINUTOS.map((u) => coberturaPoblacional(municipios, u));
}
